<template>
    <div class="horizontal-masonry" ref="container">
        <div
            v-for="(row, rIdx) in rows"
            :key="rIdx"
            class="hm-row"
            :style="{ height: row.height + 'px', marginBottom: gap + 'px' }"
        >
            <div
                v-for="(entry, iIdx) in row.items"
                :key="entry.idx"
                class="hm-cell"
                :style="{
                    width: entry.width + 'px',
                    marginRight: iIdx === row.items.length - 1 ? '0' : gap + 'px'
                }"
            >
                <img
                    :src="$url + '/' + entry.item.featured"
                    :alt="entry.item.title"
                    class="hm-img"
                    :loading="entry.idx < 6 ? 'eager' : 'lazy'"
                    :fetchpriority="entry.idx < 3 ? 'high' : 'auto'"
                    decoding="async"
                    @load="onImageLoad($event, entry.idx)"
                    @error="onImageError(entry.idx)"
                    @click="show(entry.item)"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'HorizontalMasonryComponent',
    props: {
        items: {
            type: [Object, Array],
            default: () => ({})
        },
        targetHeight: {
            type: Number,
            default: 380
        },
        gap: {
            type: Number,
            default: 14
        },
        maxPerRow: {
            type: Number,
            default: 3
        }
    },
    data() {
        return {
            containerWidth: 0,
            ratios: {},
            resizeHandler: null
        }
    },
    computed: {
        flatItems() {
            const src = this.items
            if (!src) return []
            if (Array.isArray(src)) return src
            if (Array.isArray(src.all)) return src.all
            const merged = []
            if (Array.isArray(src.col1)) merged.push(...src.col1)
            if (Array.isArray(src.col2)) merged.push(...src.col2)
            if (Array.isArray(src.col3)) merged.push(...src.col3)
            return merged
        },
        rows() {
            if (!this.containerWidth || this.flatItems.length === 0) return []

            // Pack greedily into rows, keeping each row's raw entries so we can
            // rebalance afterwards without losing the per-item aspect ratios.
            const packed = []
            let current = []
            let sumRatio = 0

            this.flatItems.forEach((item, idx) => {
                const ratio = this.ratios[idx] || 1
                current.push({ item, idx, ratio })
                sumRatio += ratio

                const gapsWidth = this.gap * (current.length - 1)
                const projectedHeight = (this.containerWidth - gapsWidth) / sumRatio
                const hitMax = current.length >= this.maxPerRow

                if (projectedHeight <= this.targetHeight || hitMax) {
                    packed.push({ entries: current, filled: true })
                    current = []
                    sumRatio = 0
                }
            })
            if (current.length > 0) {
                packed.push({ entries: current, filled: false })
            }

            // Avoid leaving a single image alone in the last row: if the last
            // row has only 1 item and the row above has at least 3, bump one
            // image down so both rows end up with >= 2.
            if (packed.length >= 2) {
                const last = packed[packed.length - 1]
                const prev = packed[packed.length - 2]
                if (last.entries.length === 1 && prev.entries.length >= 3) {
                    last.entries.unshift(prev.entries.pop())
                    last.filled = true
                }
            }

            return packed.map(row => {
                const height = row.filled
                    ? this.fillHeight(row.entries)
                    : this.targetHeight
                return {
                    height,
                    items: row.entries.map(e => ({
                        item: e.item,
                        idx: e.idx,
                        width: height * e.ratio
                    }))
                }
            })
        }
    },
    watch: {
        flatItems: {
            handler() {
                this.ratios = {}
            },
            immediate: true
        }
    },
    mounted() {
        this.updateWidth()
        this.resizeHandler = () => this.updateWidth()
        window.addEventListener('resize', this.resizeHandler)
    },
    beforeDestroy() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler)
        }
    },
    methods: {
        show(item) {
            this.$emit('show-image', item)
        },
        fillHeight(entries) {
            if (!entries.length) return this.targetHeight
            const sumRatio = entries.reduce((s, e) => s + e.ratio, 0)
            const gapsWidth = this.gap * (entries.length - 1)
            return (this.containerWidth - gapsWidth) / sumRatio
        },
        updateWidth() {
            this.$nextTick(() => {
                if (this.$refs.container) {
                    this.containerWidth = this.$refs.container.offsetWidth
                }
            })
        },
        onImageLoad(event, idx) {
            const img = event.target
            if (!img || !img.naturalWidth) return
            const ratio = img.naturalWidth / img.naturalHeight
            if (this.ratios[idx] !== ratio) {
                this.$set(this.ratios, idx, ratio)
            }
        },
        onImageError(idx) {
            if (!this.ratios[idx]) {
                this.$set(this.ratios, idx, 1)
            }
        }
    }
}
</script>

<style scoped>
.horizontal-masonry {
    width: 100%;
}

.hm-row {
    display: flex;
    flex-direction: row;
    width: 100%;
}

.hm-cell {
    height: 100%;
    overflow: hidden;
    background-color: #f4f4f4;
}

.hm-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
    display: block;
}
</style>
